import { useFormContext } from 'react-hook-form';
import { RadioField, SelectField, TextField } from '@views/register/history/history-fields';
import type { TLocalize } from '@hooks/use-localize';
import type { IHistoryDiagnosedFormValues, IHistoryDiagnosedModel } from '@/types/history/history-diagnosed';
import FieldError from '@components/field-error';

interface IProps {
    model: IHistoryDiagnosedModel;
    t: TLocalize;
}

const ConditionsSection = ({ model, t }: IProps) => {
    const {
        watch,
        register,
        formState: { errors },
    } = useFormContext<IHistoryDiagnosedFormValues>();

    const ms = model.medicalHistoryStatusOptions;
    const yn = model.yesNoUnknownOptions;
    const yes = String(model.yesValue);
    const still = String(model.medicalStatusStillBeingTreatedValue);
    const treatedPast = String(model.medicalStatusTreatedInPastValue);
    const please = t('DashLabel_PleaseSelect', 'Please Select');

    const psychiatric = watch('hasHistoryOfPsychiatric');

    return (
        <>
            <div className='section-heading'>General</div>

            <RadioField
                name="hasGastroIssues"
                label={t('DashLabel_DoYouHaveAHistoryOfGastrointestinalBowelIssues', 'Do you have a history of Gastrointestinal (bowel) issues')}
                options={yn}
                help={t('DashLabel_GastrointestinalBowelIssueExamples', "GERD, Reflux, Crohn's disease, Colitis, Diverticulitis, etc.")}
            />
            {watch('hasGastroIssues') === yes && (
                <div className="sub-question">
                    <TextField
                        name="gastroIssuesDescription"
                        label={t('DashLabel_WhatGastrointestinalIssues', 'What gastrointestinal issues')}
                        maxLength={128}
                    />
                </div>
            )}

            {!model.useReducedQuestions && (
                <RadioField
                    name="historyGallstones"
                    label={t('DashLabel_DoYouHaveAHistoryOfGallstonesGallbladderDisease', 'Do you have a history of Gallstones/Gallbladder disease')}
                    options={ms}
                />
            )}

            <RadioField
                name="historyPancreatitis"
                label={t('DashLabel_DoYouHaveAHistoryOfPancreatitis', 'Do you have a history of Pancreatitis')}
                options={ms}
            />

            {!model.isLiverRegistration && (
                <>
                    <RadioField
                        name="historyLiverDisease"
                        label={t('DashLabel_DoYouHaveAHistoryOfLiverDisease', 'Do you have a history of Liver Disease')}
                        options={ms}
                    />
                    {!model.useReducedQuestions && (
                        <>
                            <RadioField
                                name="hasHepB"
                                label={t('DashLabel_DoYouHaveAHistoryOfHepatitisB', 'Do you have a history of Hepatitis B')}
                                options={ms}
                            />
                            <RadioField
                                name="hasHepC"
                                label={t('DashLabel_DoYouHaveAHistoryOfHepatitisC', 'Do you have a history of Hepatitis C')}
                                options={ms}
                            />
                        </>
                    )}
                </>
            )}

            <RadioField
                name="hasBleedingProblem"
                label={t('DashLabel_HaveYouEverBeenToldYouHaveABleedingProblem', 'Have you ever been told you have a bleeding problem')}
                options={ms}
            />

            {!model.useReducedQuestions && (
                <>
                    <RadioField
                        name="hasHadUti"
                        label={t('DashLabel_HaveYouEverHadAUrinaryTractInfectionUti', 'Have you ever had a Urinary Tract Infection (UTI)')}
                        options={yn}
                    />
                    {watch('hasHadUti') === yes && (
                        <div className="sub-question">
                            <div className="form-group">
                                <label className="control-label col-xs-12">
                                    {t('DashLabel_HowManyTimesHaveYouHadAUti', 'How many times have you had a UTI')}?
                                    <FieldError message={errors.utiEpisodes?.message} />
                                </label>
                                <div className="col-xs-12">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <label className="radio-inline" key={n}>
                                            <input type="radio" value={n} {...register('utiEpisodes')} />{' '}
                                            {n === 5 ? '5+' : n}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <SelectField
                                name="utiRemissionStatus"
                                label={t('DashLabel_HowLongSinceYourLastUti', 'How long since your last UTI')}
                                options={model.utiRemissionOptions}
                                placeholder={please}
                            />
                        </div>
                    )}
                </>
            )}

            <RadioField
                name="historyKidneyDamage"
                label={t('DashLabel_HaveYouEverHadInjuryToOneOrBothKidneys', 'Have you ever had injury to one or both kidneys')}
                options={ms}
            />

            {!model.useReducedQuestions && (
                <>
                    <RadioField
                        name="historyProteinuria"
                        label={t('DashLabel_DoYouHaveAHistoryOfProteinInYourUrineProteinuria', 'Do you have a history of protein in your urine (Proteinuria)')}
                        options={ms}
                    />
                    <RadioField
                        name="historyHematuria"
                        label={t('DashLabel_DoYouHaveAHistoryOfBloodInYourUrineHematuria', 'Do you have a history of blood in your urine (Hematuria)')}
                        options={ms}
                    />
                </>
            )}

            <RadioField
                name="historyNeuroDisease"
                label={t('DashLabel_DoYouHaveAHistoryOfNeurologicalDisease', 'Do you have a history of neurological disease')}
                options={ms}
            />
            <RadioField
                name="historySeizure"
                label={t('DashLabel_DoYouHaveAHistoryOfSeizures', 'Do you have a history of seizures')}
                options={ms}
            />
            <RadioField
                name="hasLupus"
                label={t('DashLabel_DoYouHaveAHistoryOfLupus', 'Do you have a history of Lupus')}
                options={ms}
            />
            <RadioField
                name="historyParalysis"
                label={t('DashLabel_DoYouHaveAHistoryOfParalysis', 'Do you have a history of paralysis')}
                options={ms}
            />
            <RadioField
                name="hasArthritis"
                label={t('DashLabel_DoYouHaveAHistoryOfArthritis', 'Do you have a history of arthritis')}
                options={ms}
            />
            <RadioField
                name="historyNeuropathy"
                label={t('DashLabel_DoYouHaveAHistoryOfNeuropathy', 'Do you have a history of Neuropathy')}
                options={ms}
            />
            <RadioField
                name="historyDepression"
                label={t('DashLabel_DoYouHaveAHistoryOfDepression', 'Do you have a history of depression')}
                options={ms}
            />
            <RadioField
                name="historyAnxiety"
                label={t('DashLabel_DoYouHaveAHistoryOfAnxiety', 'Do you have a history of anxiety')}
                options={ms}
            />

            <RadioField
                name="hasHistoryOfPsychiatric"
                label={t('DashLabel_DoYouHaveAHistoryOfOtherDocumentedPsychiatricIllne', 'Do you have a history of other documented Psychiatric illness')}
                options={ms}
                help={t('DashLabel_PanicAttacksSuicide', 'Panic attacks, suicide attempts, or psychiatric hospitalizations')}
            />
            {(psychiatric === still || psychiatric === treatedPast) && (
                <div className="sub-question">
                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t('DashLabel_PleaseDescribe', 'Please describe')}?
                            <FieldError message={errors.psychiatricDescription?.message} />
                        </label>
                        <div className="col-xs-12 col-sm-8">
                            <input className="form-control" maxLength={128} {...register('psychiatricDescription')} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ConditionsSection;
